/* ============================================================
   KSP LUXE — MINI CART  (assets/ksp-cart.js)
   ------------------------------------------------------------
   Registers <cart-notification>. That element name is deliberate:
   assets/product-form.js resolves its cart target as

       querySelector("cart-notification") || querySelector("cart-drawer")

   so registering under this name means add-to-cart binds to THIS
   component first, whatever state <cart-drawer> is in. Nothing here
   depends on settings.cart_type, on cart.js, on cart-drawer.js, or on
   the Section Rendering API — cart state is read straight from /cart.js.

   Behaviour:
     • add to cart  → no navigation; panel peeks open, then closes
     • hover icon   → panel opens (any hover-capable pointer)
     • click icon   → navigates to the cart page, always, every device

   Positioning note: the theme renders the header cart snippet more than
   once (desktop / mobile / sticky), so #cart-icon-bubble is a duplicate
   id. Anchoring must use the first VISIBLE icon — a hidden copy has a
   zero rect, which is what parked the panel at the top-left. If no
   visible icon exists, fall back to the top-right of the viewport.

   Hover note: capability is checked at EVENT time via
   matchMedia('(hover: hover)') — not once at load — and there is no
   viewport-width gate. The earlier build evaluated a 990px width gate
   once during init, which silently disabled hover for any window that
   happened to be narrower at load.
   ============================================================ */

(function () {
  "use strict";

  if (customElements.get("cart-notification")) return;

  var OPEN_DELAY = 90;
  var CLOSE_DELAY = 340;
  var PEEK_MS = 4600;
  var ICON_SEL = "#cart-icon-bubble, .header__icon--cart";

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || "/cart";
  }

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-IN", {
        style: "currency",
        currency: currency || "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function initial(text) {
    var t = String(text || "").trim();
    return t ? t.charAt(0).toUpperCase() : "K";
  }

  function isVisible(el) {
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function hoverAllowed() {
    return window.matchMedia("(hover: hover)").matches;
  }

  class KspCart extends HTMLElement {
    connectedCallback() {
      if (this.__ksReady) return;
      this.__ksReady = true;

      this.removeAttribute("hidden");
      this.innerHTML =
        '<div class="ksp-cart__panel" role="dialog" aria-label="Cart preview" aria-live="polite"></div>';
      this.panel = this.querySelector(".ksp-cart__panel");

      this.openTimer = null;
      this.closeTimer = null;
      this.peekTimer = null;

      this.panel.addEventListener("mouseenter", this.cancelClose.bind(this));
      this.panel.addEventListener("mouseleave", this.scheduleClose.bind(this));

      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") this.close();
      }.bind(this));

      var reposition = function () {
        if (this.classList.contains("is-open")) this.position();
      }.bind(this);
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, { passive: true });

      this.bindIcon();
      this.refresh();
    }

    /* ---- product-form.js contract ---------------------------------- */

    getSectionsToRender() {
      // Kept deliberately minimal. This component re-reads /cart.js rather
      // than swapping rendered section HTML, so it needs no real sections —
      // but product-form.js maps over the result, so it must be an array,
      // and an existing section id keeps the request parameter valid.
      return [{ id: "cart-icon-bubble" }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents() {
      this.refresh({ peek: true });
    }

    /* ---- icon wiring ------------------------------------------------ */

    iconCandidates() {
      // querySelectorAll (not getElementById): the id is duplicated across
      // header variants, and we must be able to see every copy.
      var list = document.querySelectorAll(ICON_SEL);
      var out = [];
      for (var i = 0; i < list.length; i++) {
        if (out.indexOf(list[i]) === -1 && !this.contains(list[i])) out.push(list[i]);
      }
      return out;
    }

    get icon() {
      var candidates = this.iconCandidates();
      for (var i = 0; i < candidates.length; i++) {
        if (isVisible(candidates[i])) return candidates[i];
      }
      return candidates[0] || null;
    }

    bindIcon() {
      var self = this;

      // Capture phase, bound on document, so this runs BEFORE any listener
      // attached directly to the icon. cart-drawer.js binds a click handler
      // that calls preventDefault() and opens the drawer; stopping the event
      // here is what makes click-to-navigate reliable even if that file loads.
      document.addEventListener(
        "click",
        function (e) {
          var target = e.target;
          if (!target || !target.closest) return;
          if (!target.closest(ICON_SEL)) return;
          e.preventDefault();
          e.stopPropagation();
          self.close();
          window.location.href = cartUrl();
        },
        true
      );

      // Icons are links again, not dialog triggers.
      this.iconCandidates().forEach(function (icon) {
        icon.removeAttribute("role");
        icon.setAttribute("aria-haspopup", "false");
      });

      // Delegated on document rather than bound per element, so hover works
      // on every copy of the icon — including sticky-header clones injected
      // after this component initialises. Listeners are attached
      // unconditionally; capability is evaluated per event.
      document.addEventListener("mouseover", function (e) {
        if (!hoverAllowed()) return;
        if (e.target && e.target.closest && e.target.closest(ICON_SEL)) {
          self.scheduleOpen();
        }
      });
      document.addEventListener("mouseout", function (e) {
        if (!hoverAllowed()) return;
        if (e.target && e.target.closest && e.target.closest(ICON_SEL)) {
          self.scheduleClose();
        }
      });
      document.addEventListener("focusin", function (e) {
        if (e.target && e.target.closest && e.target.closest(ICON_SEL)) {
          self.open();
        }
      });
    }

    /* ---- open / close ----------------------------------------------- */

    scheduleOpen() {
      this.cancelClose();
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(this.open.bind(this), OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(this.close.bind(this), CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.peekTimer);
    }

    open() {
      clearTimeout(this.closeTimer);
      this.position();
      this.classList.add("is-open");
    }

    close() {
      clearTimeout(this.peekTimer);
      this.classList.remove("is-open");
    }

    peek() {
      this.open();
      clearTimeout(this.peekTimer);
      this.peekTimer = setTimeout(this.close.bind(this), PEEK_MS);
    }

    position() {
      if (!this.panel) return;
      var w = this.panel.offsetWidth || 384;

      // Fallback: top-right of the viewport (never the origin).
      var left = window.innerWidth - w - 16;
      var top = 24;

      var icon = this.icon;
      if (icon && isVisible(icon)) {
        var r = icon.getBoundingClientRect();
        left = Math.min(Math.max(16, r.right - w), window.innerWidth - w - 16);
        top = r.bottom + 12;
      }

      this.panel.style.left = Math.round(left) + "px";
      this.panel.style.top = Math.round(top) + "px";
    }

    /* ---- data ------------------------------------------------------- */

    refresh(opts) {
      opts = opts || {};
      var self = this;
      return fetch(cartUrl() + ".js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.render(cart);
          self.updateBubble(cart.item_count);
          if (opts.peek) self.peek();
        })
        .catch(function (e) {
          console.error("[ksp-cart] could not read cart state:", e);
        });
    }

    updateBubble(count) {
      // Every copy of the icon, not just the visible one, so the count is
      // right when the layout switches between header variants.
      this.iconCandidates().forEach(function (icon) {
        var bubble = icon.querySelector(".cart-count-bubble");
        if (bubble) {
          var n = bubble.querySelector("span[aria-hidden='true']");
          if (n) n.textContent = count;
          bubble.style.display = count > 0 ? "" : "none";
        }
        icon.classList.toggle("ksp-has-items", count > 0);
      });
    }

    render(cart) {
      if (!cart || !cart.item_count) {
        this.panel.innerHTML =
          '<div class="ksp-cart__empty">' +
          '<div class="ksp-cart__empty-mark">\u2732</div>' +
          '<p class="ksp-cart__empty-title">Nothing here yet</p>' +
          '<p class="ksp-cart__empty-text">Your care starts with a single step. Explore the programmes when you are ready.</p>' +
          '<a class="ksp-cart__btn ksp-cart__btn--secondary" href="/collections/all">Browse programmes</a>' +
          "</div>";
        return;
      }

      var items = cart.items
        .map(function (item) {
          var thumb = item.image
            ? '<img class="ksp-cart__thumb" src="' +
              esc(item.image.replace(/(\.[a-z]+)(\?|$)/i, "_120x$1$2")) +
              '" alt="" loading="lazy" width="54" height="54">'
            : '<div class="ksp-cart__thumb ksp-cart__thumb--blank">' +
              esc(initial(item.product_title)) +
              "</div>";

          var variant =
            item.variant_title && item.variant_title !== "Default Title"
              ? '<p class="ksp-cart__variant">' + esc(item.variant_title) + "</p>"
              : "";

          return (
            '<li class="ksp-cart__item">' +
            thumb +
            '<div class="ksp-cart__meta">' +
            '<a class="ksp-cart__name" href="' + esc(item.url) + '">' +
            esc(item.product_title) +
            "</a>" +
            variant +
            '<div class="ksp-cart__line">' +
            '<span class="ksp-cart__qty">\u00d7' + item.quantity + "</span>" +
            '<span class="ksp-cart__price">' +
            esc(money(item.final_line_price, cart.currency)) +
            "</span>" +
            "</div>" +
            "</div>" +
            "</li>"
          );
        })
        .join("");

      var word = cart.item_count === 1 ? "item" : "items";

      this.panel.innerHTML =
        '<div class="ksp-cart__head">' +
        '<p class="ksp-cart__eyebrow">' + cart.item_count + " " + word + " in your cart</p>" +
        '<p class="ksp-cart__title">Ready when <em>you are</em></p>' +
        "</div>" +
        '<ul class="ksp-cart__items">' + items + "</ul>" +
        '<div class="ksp-cart__foot">' +
        '<div class="ksp-cart__totals">' +
        '<span class="ksp-cart__totals-label">Subtotal</span>' +
        '<span class="ksp-cart__totals-value">' +
        esc(money(cart.total_price, cart.currency)) +
        "</span>" +
        "</div>" +
        '<p class="ksp-cart__note">Taxes and shipping calculated at checkout.</p>' +
        '<div class="ksp-cart__actions">' +
        '<a class="ksp-cart__btn ksp-cart__btn--secondary" href="' + esc(cartUrl()) + '">View cart</a>' +
        '<a class="ksp-cart__btn ksp-cart__btn--primary" href="/checkout">Checkout</a>' +
        "</div>" +
        "</div>";
    }
  }

  customElements.define("cart-notification", KspCart);
})();
